<p-dialog
    [header]="title()"
    [visible]="visible()"
    (visibleChange)="visible.set($event)"
    [style]="{ width: '36rem' }"
    [modal]="true"
    [closable]="!saving()"
>
    <p-fluid>
        <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1">
                <label for="companyName" class="font-semibold">Name</label>
                <input
                    pInputText
                    id="companyName"
                    [ngModel]="name()"
                    (ngModelChange)="name.set($event)"
                    placeholder="Acme Holdings Ltd."
                    [disabled]="saving()"
                />
                @if (nameError()) {
                    <small class="text-red-500">{{ nameError() }}</small>
                }
            </div>

            <div class="flex flex-col gap-1">
                <label for="companyCountry" class="font-semibold">Country</label>
                <p-select
                    inputId="companyCountry"
                    [options]="countryOptions"
                    optionLabel="label"
                    optionValue="value"
                    [ngModel]="countryCode()"
                    (ngModelChange)="countryCode.set($event)"
                    [filter]="true"
                    [showClear]="true"
                    placeholder="Select a country"
                    appendTo="body"
                    [disabled]="saving()"
                >
                    <ng-template let-option #item>
                        <div class="flex items-center gap-2">
                            <span [class]="'flag flag-' + option.value.toLowerCase()" style="width: 18px; height: 12px"></span>
                            <span>{{ option.label }}</span>
                        </div>
                    </ng-template>
                    <ng-template let-option #selectedItem>
                        @if (option) {
                            <div class="flex items-center gap-2">
                                <span [class]="'flag flag-' + option.value.toLowerCase()" style="width: 18px; height: 12px"></span>
                                <span>{{ option.label }}</span>
                            </div>
                        }
                    </ng-template>
                </p-select>
                @if (countryError()) {
                    <small class="text-red-500">{{ countryError() }}</small>
                }
            </div>

            <div class="flex flex-col gap-1">
                <label for="companyAddress" class="font-semibold">Address</label>
                <textarea
                    pTextarea
                    id="companyAddress"
                    rows="3"
                    [ngModel]="address()"
                    (ngModelChange)="address.set($event)"
                    [disabled]="saving()"
                ></textarea>
                @if (addressError()) {
                    <small class="text-red-500">{{ addressError() }}</small>
                }
            </div>

            <div class="flex flex-col gap-1">
                <label for="companyReg" class="font-semibold">Registration details</label>
                <textarea
                    pTextarea
                    id="companyReg"
                    rows="3"
                    [ngModel]="regDetails()"
                    (ngModelChange)="regDetails.set($event)"
                    placeholder="Reg. no. / VAT / Tax ID"
                    [disabled]="saving()"
                ></textarea>
                @if (regError()) {
                    <small class="text-red-500">{{ regError() }}</small>
                }
            </div>

            <div class="flex flex-col gap-1">
                <label for="companyLiveChat" class="font-semibold">LiveChat Group ID</label>
                <input
                    pInputText
                    id="companyLiveChat"
                    inputmode="numeric"
                    pattern="\d+"
                    [ngModel]="liveChatGroupId()"
                    (ngModelChange)="liveChatGroupId.set($event)"
                    placeholder="Leave empty if not using LiveChat"
                    [disabled]="saving()"
                />
                @if (liveChatGroupIdError()) {
                    <small class="text-red-500">{{ liveChatGroupIdError() }}</small>
                } @else {
                    <small class="text-muted-color">Numeric LiveChat Group ID. License is platform-global and managed in backend config.</small>
                }
            </div>

            <div class="flex flex-col gap-3 pt-4 border-t border-surface-200 dark:border-surface-700">
                <div class="flex items-center justify-between">
                    <div class="font-semibold">Stripe</div>
                    <div class="flex items-center gap-2">
                        <p-checkbox
                            inputId="stripeEnabled"
                            [binary]="true"
                            [ngModel]="stripeIsEnabled()"
                            (ngModelChange)="stripeIsEnabled.set($event)"
                            [disabled]="saving()"
                        />
                        <label for="stripeEnabled">Enabled</label>
                    </div>
                </div>
                <small class="text-muted-color">
                    One Stripe account serves every website owned by this company. Per-website provider choice lives on the website's Payment tab.
                </small>

                <div class="flex flex-col gap-1">
                    <label for="stripePub" class="font-medium">Publishable Key</label>
                    <input
                        pInputText
                        id="stripePub"
                        [ngModel]="stripePublishableKey()"
                        (ngModelChange)="stripePublishableKey.set($event)"
                        placeholder="pk_live_…  /  pk_test_…"
                        [disabled]="saving()"
                    />
                    @if (stripePublishableKeyError()) {
                        <small class="text-red-500">{{ stripePublishableKeyError() }}</small>
                    }
                </div>

                <div class="flex flex-col gap-1">
                    <label for="stripeSec" class="font-medium">Secret Key</label>
                    <p-password
                        inputId="stripeSec"
                        [ngModel]="stripeSecretKey()"
                        (ngModelChange)="stripeSecretKey.set($event)"
                        [feedback]="false"
                        [toggleMask]="true"
                        placeholder="sk_live_…  /  sk_test_…"
                        styleClass="w-full"
                        inputStyleClass="w-full"
                        [disabled]="saving()"
                    />
                    @if (stripeSecretKeyError()) {
                        <small class="text-red-500">{{ stripeSecretKeyError() }}</small>
                    }
                </div>

                <div class="flex flex-col gap-1">
                    <label for="stripeHook" class="font-medium">Webhook Secret</label>
                    <p-password
                        inputId="stripeHook"
                        [ngModel]="stripeWebhookSecret()"
                        (ngModelChange)="stripeWebhookSecret.set($event)"
                        [feedback]="false"
                        [toggleMask]="true"
                        placeholder="whsec_…"
                        styleClass="w-full"
                        inputStyleClass="w-full"
                        [disabled]="saving()"
                    />
                    @if (stripeWebhookSecretError()) {
                        <small class="text-red-500">{{ stripeWebhookSecretError() }}</small>
                    }
                </div>

                <div class="flex flex-col gap-1">
                    <label class="font-medium">Webhook URL</label>
                    <div class="flex items-center gap-2">
                        <code class="flex-1 truncate font-mono text-sm px-3 py-2 rounded bg-surface-100 dark:bg-surface-800">{{ webhookUrl }}</code>
                        <p-button
                            [icon]="webhookCopied() ? 'pi pi-check' : 'pi pi-copy'"
                            severity="secondary"
                            [text]="true"
                            [rounded]="true"
                            size="small"
                            [pTooltip]="webhookCopied() ? 'Copied' : 'Copy'"
                            (onClick)="copyWebhookUrl()"
                        />
                    </div>
                    <small class="text-muted-color">Paste this URL into your Stripe Dashboard webhook configuration.</small>
                </div>
            </div>

            @if (generalError()) {
                <small class="text-red-500">{{ generalError() }}</small>
            }
        </div>
    </p-fluid>
    <ng-template #footer>
        <div class="flex justify-end gap-2">
            <p-button label="Cancel" severity="secondary" [disabled]="saving()" (onClick)="close()" />
            <p-button label="Save" icon="pi pi-check" [loading]="saving()" (onClick)="save()" />
        </div>
    </ng-template>
</p-dialog>
